/**
 * FirstRunTour – 4-step full-screen overlay shown once to new learners so the
 * app feels taught, not thrown at them.
 *
 * Triggered by `!user.tutorial_completed` on HomePage. We persist completion
 * via POST /api/me/tutorial-complete so it never shows twice.
 */
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  MicrophoneStage, ChatCircleDots, Translate, BookmarkSimple, CaretRight, X,
} from '@phosphor-icons/react';

const STEPS = [
  {
    icon: <MicrophoneStage size={28} weight="duotone" />,
    title: 'Tap the orb to speak',
    body: 'Every session is voice-first. Tap, say what you want to say — the AI auto-stops when you pause and replies out loud.',
  },
  {
    icon: <ChatCircleDots size={28} weight="duotone" />,
    title: 'Three ways to practice',
    body: 'Free Talk for real conversation · Guided Scenarios for role-play · Repeat Practice for beginners who just want to say short phrases after the AI.',
  },
  {
    icon: <Translate size={28} weight="duotone" />,
    title: 'Tap any word to learn it',
    body: 'In every transcript, tap a word to see its meaning, pronunciation (with the stressed syllable highlighted), and an example sentence.',
  },
  {
    icon: <BookmarkSimple size={28} weight="duotone" />,
    title: 'Save words you like',
    body: 'Tap Save on any word, definition, or session review to add it to your personal vocabulary. Practice them anytime from Home.',
  },
];

export default function FirstRunTour({ open, onDone }) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];

  function next() {
    if (step < STEPS.length - 1) setStep(step + 1);
    else onDone?.();
  }

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="first-run-tour"
        >
          <motion.div
            key={step}
            className="relative w-full max-w-sm rounded-3xl bg-[var(--lf-surface)] p-6"
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
          >
            <button
              onClick={() => onDone?.()}
              className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-[var(--lf-surface-muted)]"
              data-testid="tour-skip-btn"
            >
              <X size={14} />
            </button>

            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[#FFF5EE] text-[var(--lf-primary)]">
              {current.icon}
            </div>
            <div className="mt-4 text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
              Step {step + 1} of {STEPS.length}
            </div>
            <h2 className="mt-1 text-2xl font-bold">{current.title}</h2>
            <p className="mt-2 text-[var(--lf-text-muted)]">{current.body}</p>

            <div className="mt-5 flex items-center justify-between">
              <div className="flex gap-1.5">
                {STEPS.map((_, i) => (
                  <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-[var(--lf-primary)]' : 'w-1.5 bg-[var(--lf-border)]'}`} />
                ))}
              </div>
              <button onClick={next} className="lf-btn lf-btn-primary py-2 text-sm" data-testid="tour-next-btn">
                {step === STEPS.length - 1 ? 'Start practicing' : 'Next'}
                <CaretRight size={14} weight="bold" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
